import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import s from './EaChartControl.module.scss';
import StrategyModal from './StrategyModal.jsx';
import {
  EA_OPEN_MODAL_EVENT,
  EA_CHART_TOOLBAR_TEARDOWN,
  EA_CHART_TOOLBAR_READY,
} from './eaApi.js';
import useEaModuleVisible from './useEaModuleVisible.js';
import { useAuthContext } from '../../../contexts/Auth-Context.js';
import { useChartContext } from '../../../contexts/Chart-Context.js';
import { useSymbolContext } from '../../../contexts/Symbol-Context.js';

import StrategyBulbIcon from './StrategyBulbIcon.jsx';

const HOST_ID = 'ea-chart-control';
/** Fallback if Chart.jsx events are missed — wait for chartSetup() to finish */
const CHART_IDLE_MS = 1500;

/**
 * Strategy button lives inside #chartcontrols (same flex row as timeframe, indicators…)
 * so it aligns with the other toolbar icons — not on the far-right price axis.
 *
 * Safe attach: leave the toolbar BEFORE amCharts rebuilds it (style / symbol / TF /
 * bullets / reconnect), then re-insert after StockToolbar exists. Reusing a node
 * that amCharts already disposed is what made the icon gold / duplicated / missing.
 */
export default function EaChartControl() {
  const { t } = useTranslation();
  const { platFromData, selectedAuthSymbol, isReconnected } = useAuthContext();
  const { selectedTimeFrame, selectedStyle } = useChartContext();
  const { showTooltip, showBullets } = useSymbolContext();
  const userId = platFromData?.[5]?.userId;
  const eaModuleOn = useEaModuleVisible(userId);
  const eaModuleOnRef = useRef(eaModuleOn);
  eaModuleOnRef.current = eaModuleOn;
  const buyColor = selectedStyle?.buyColor;
  const sellColor = selectedStyle?.sellColor;

  const [host, setHost] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  const idleTimerRef = useRef(null);
  const skipRebuildRef = useRef(true);

  const detachHost = () => {
    const node = document.getElementById(HOST_ID);
    if (node?.parentNode) node.parentNode.removeChild(node);
    setHost(null);
  };

  const attachHost = () => {
    if (!eaModuleOnRef.current) return false;
    const controls = document.getElementById('chartcontrols');
    if (!controls) return false;

    let node = document.getElementById(HOST_ID);
    // Never reuse a node that is no longer a child of the live toolbar
    // (amCharts rebuild leaves a detached/poisoned host).
    if (node && node.parentNode !== controls) {
      try { node.remove(); } catch { /* already gone */ }
      node = null;
    }
    if (!node) {
      node = document.createElement('div');
      node.id = HOST_ID;
      node.className = `am5stock am5stock-control am5stock-control-button ${s.host}`;
      node.setAttribute('role', 'button');
      node.setAttribute('tabindex', '0');
    }

    const stocks = controls.querySelectorAll(':scope > .am5stock');
    const anchor = stocks.length ? stocks[stocks.length - 1] : null;

    const needsMove = node.parentNode !== controls
      || (anchor && node.previousElementSibling !== anchor);

    if (needsMove) {
      try {
        if (anchor) {
          anchor.insertAdjacentElement('afterend', node);
        } else {
          controls.appendChild(node);
        }
      } catch {
        return false;
      }
    }

    setHost(node);
    return true;
  };

  const scheduleAttach = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null;
      attachHost();
    }, CHART_IDLE_MS);
  };

  // Chart.jsx signals: teardown before dispose, ready after StockToolbar.new
  useEffect(() => {
    const onTeardown = () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      detachHost();
    };
    const onReady = () => {
      requestAnimationFrame(() => attachHost());
    };
    window.addEventListener(EA_CHART_TOOLBAR_TEARDOWN, onTeardown);
    window.addEventListener(EA_CHART_TOOLBAR_READY, onReady);
    return () => {
      window.removeEventListener(EA_CHART_TOOLBAR_TEARDOWN, onTeardown);
      window.removeEventListener(EA_CHART_TOOLBAR_READY, onReady);
    };
  }, []);

  // Initial mount — poll until #chartcontrols exists, attach once, then stop
  useEffect(() => {
    let cancelled = false;
    const pollId = setInterval(() => {
      if (cancelled) return;
      if (attachHost()) clearInterval(pollId);
    }, 300);
    const stopPoll = setTimeout(() => clearInterval(pollId), 15000);

    return () => {
      cancelled = true;
      clearInterval(pollId);
      clearTimeout(stopPoll);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      detachHost();
    };
  }, []);

  // Any Chart.jsx chartSetup() trigger — fallback if the ready event is missed
  useEffect(() => {
    if (skipRebuildRef.current) {
      skipRebuildRef.current = false;
      return undefined;
    }
    detachHost();
    scheduleAttach();
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [
    selectedAuthSymbol,
    selectedTimeFrame,
    buyColor,
    sellColor,
    showTooltip,
    showBullets,
    isReconnected,
  ]);

  useEffect(() => {
    const win = (() => { try { return window.top; } catch { return window; } })();
    const onOpen = (e) => {
      setEditing(e?.detail?.strategy || null);
      setReadOnly(!!e?.detail?.readOnly);
      setOpen(true);
    };
    win.addEventListener(EA_OPEN_MODAL_EVENT, onOpen);
    return () => win.removeEventListener(EA_OPEN_MODAL_EVENT, onOpen);
  }, []);

  const close = () => { setOpen(false); setEditing(null); setReadOnly(false); };

  useEffect(() => {
    close();
  }, [selectedAuthSymbol]);

  // Group EA OFF → remove chart Strategy button + close builder (positions stay).
  useEffect(() => {
    if (!eaModuleOn) {
      close();
      detachHost();
      return undefined;
    }
    attachHost();
    return undefined;
  }, [eaModuleOn]);

  useEffect(() => {
    if (!host) return;
    const label = t('ea.strategy', 'Strategy');
    host.setAttribute('title', label);
    host.setAttribute('data-tooltip', label);
    host.setAttribute('aria-label', label);
  }, [host, t]);

  useEffect(() => {
    if (!host) return;
    host.classList.toggle('am5stock-control-button-active', open);
  }, [host, open]);

  useEffect(() => {
    if (!host) return;
    const trigger = (e) => {
      e?.preventDefault?.();
      setEditing(null);
      setReadOnly(false);
      setOpen(true);
    };
    const onKey = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        trigger(e);
      }
    };
    host.addEventListener('click', trigger);
    host.addEventListener('keydown', onKey);
    return () => {
      host.removeEventListener('click', trigger);
      host.removeEventListener('keydown', onKey);
    };
  }, [host]);

  if (!eaModuleOn) return null;

  return (
    <>
      {host && createPortal(
        <div className={`am5stock-control-icon ${s.iconWrapper}`}>
          <div className={s.iconDiv}>
            <StrategyBulbIcon className={s.icon} />
          </div>
        </div>,
        host
      )}

      <StrategyModal
        open={open}
        onClose={close}
        userId={userId}
        symbol={selectedAuthSymbol || ''}
        timeframe={selectedTimeFrame || '5m'}
        editing={editing}
        readOnly={readOnly}
      />
    </>
  );
}
